import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AppShell from '@/components/AppShell';
import { AnnotationToolbar, HIGHLIGHT_COLORS } from '@/components/annotation/AnnotationToolbar';
import { AnnotationSidebar } from '@/components/annotation/AnnotationSidebar';
import SourceViewer from '@/components/annotation/SourceViewer';
import { StudentLoginModal, NoteEditorModal } from '@/components/annotation/AnnotationModals';
import { useDrawingCanvas } from '@/components/annotation/useDrawingCanvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnnotationTool() {
  const { unitId, sourceIndex } = useParams();
  const navigate = useNavigate();

  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState('');
  const [studentName, setStudentName] = useState('');
  const [showStudentModal, setShowStudentModal] = useState(true);

  // Annotation state
  const [annotation, setAnnotation] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [marginNotes, setMarginNotes] = useState([]);
  const [isShared, setIsShared] = useState(false);
  const [teacherFeedback, setTeacherFeedback] = useState('');

  // Tool state
  const [activeTool, setActiveTool] = useState('highlight'); // 'highlight' | 'note' | 'draw'
  const [selectedColor, setSelectedColor] = useState('yellow');
  const [drawingColor, setDrawingColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);

  // Note editor state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNotePosition, setNewNotePosition] = useState(0);
  const [editingNoteId, setEditingNoteId] = useState(null);

  // Drawing canvas (extracted hook)
  const {
    canvasRef, drawings, setDrawings,
    startDrawing, draw, stopDrawing, clearDrawings,
  } = useDrawingCanvas({
    color: drawingColor,
    strokeWidth,
    enabled: showDrawingCanvas,
  });

  useEffect(() => {
    const savedId = localStorage.getItem('annotation_student_id');
    const savedName = localStorage.getItem('annotation_student_name');
    if (savedId && savedName) {
      setStudentId(savedId);
      setStudentName(savedName);
      setShowStudentModal(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [, sourcesRes] = await Promise.all([
        axios.get(`${API}/units/${unitId}`),
        axios.get(`${API}/primary-sources/${unitId}`),
      ]);
      const sources = sourcesRes.data.sources || [];
      const idx = parseInt(sourceIndex, 10);
      if (idx >= 0 && idx < sources.length) {
        setSource({ ...sources[idx], id: `${unitId}-source-${idx}` });
      }

      const annotationRes = await axios.get(`${API}/annotations/${studentId}/${unitId}-source-${sourceIndex}`);
      if (annotationRes.data) {
        setAnnotation(annotationRes.data);
        setHighlights(annotationRes.data.highlights || []);
        setMarginNotes(annotationRes.data.margin_notes || []);
        setDrawings(annotationRes.data.drawings || []);
        setIsShared(annotationRes.data.is_shared || false);
        setTeacherFeedback(annotationRes.data.teacher_feedback || '');
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load source');
      }
    } finally {
      setLoading(false);
    }
  }, [unitId, sourceIndex, studentId, setDrawings]);

  useEffect(() => {
    if (studentId) fetchData();
  }, [studentId, fetchData]);

  const handleStudentLogin = () => {
    if (!studentId.trim() || !studentName.trim()) {
      toast.error('Please enter both student ID and name');
      return;
    }
    localStorage.setItem('annotation_student_id', studentId);
    localStorage.setItem('annotation_student_name', studentName);
    setShowStudentModal(false);
  };

  const handleTextSelection = () => {
    if (activeTool !== 'highlight') return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;
    const text = selection.toString().trim();
    if (!text) return;
    const range = selection.getRangeAt(0);
    const container = document.getElementById('source-content');
    if (!container || !container.contains(range.commonAncestorContainer)) return;

    setHighlights([...highlights, {
      id: `highlight-${Date.now()}`,
      start_offset: range.startOffset,
      end_offset: range.endOffset,
      color: selectedColor,
      text,
      note: '',
    }]);
    selection.removeAllRanges();
    toast.success('Text highlighted!');
  };

  const handleAddMarginNote = (e) => {
    if (activeTool !== 'note') return;
    const container = document.getElementById('source-content');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const positionY = ((e.clientY - rect.top) / rect.height) * 100;
    setNewNotePosition(Math.max(0, Math.min(100, positionY)));
    setShowNoteModal(true);
  };

  const saveMarginNote = () => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter note content');
      return;
    }
    if (editingNoteId) {
      setMarginNotes(marginNotes.map((n) => (n.id === editingNoteId ? { ...n, content: newNoteContent } : n)));
    } else {
      setMarginNotes([...marginNotes, {
        id: `note-${Date.now()}`,
        position_y: newNotePosition,
        content: newNoteContent,
        created_at: new Date().toISOString(),
      }]);
    }
    setShowNoteModal(false);
    setNewNoteContent('');
    setEditingNoteId(null);
    toast.success(editingNoteId ? 'Note updated!' : 'Note added!');
  };

  const deleteHighlight = (id) => {
    setHighlights(highlights.filter((h) => h.id !== id));
    toast.success('Highlight removed');
  };
  const deleteNote = (id) => {
    setMarginNotes(marginNotes.filter((n) => n.id !== id));
    toast.success('Note removed');
  };

  const renderHighlightedContent = () => {
    if (!source) return null;
    const content = source.full_text || source.description || '';
    if (highlights.length === 0) return [content];

    let segments = [content];
    highlights.forEach((h) => {
      const colorClass = HIGHLIGHT_COLORS.find((c) => c.value === h.color);
      const bgColor = colorClass ? colorClass.bg : 'bg-[#F5EBC0]';
      segments = segments.flatMap((seg, segIdx) => {
        if (typeof seg !== 'string' || !seg.includes(h.text)) return [seg];
        const parts = seg.split(h.text);
        const result = [];
        parts.forEach((part, i) => {
          if (i > 0) {
            result.push(
              <mark
                key={`${h.id}-${segIdx}-${i}`}
                className={`${bgColor} px-1 rounded cursor-pointer`}
                data-highlight-id={h.id}
              >
                {h.text}
              </mark>
            );
          }
          if (part) result.push(part);
        });
        return result;
      });
    });
    return segments;
  };

  const saveAnnotation = async () => {
    try {
      const data = { highlights, margin_notes: marginNotes, drawings, is_shared: isShared };
      if (annotation?.id) {
        await axios.put(`${API}/annotations/${annotation.id}`, data);
      } else {
        const createRes = await axios.post(`${API}/annotations`, {
          source_id: `${unitId}-source-${sourceIndex}`,
          unit_id: unitId,
          student_id: studentId,
          student_name: studentName,
        });
        await axios.put(`${API}/annotations/${createRes.data.id}`, data);
        setAnnotation({ id: createRes.data.id });
      }
      toast.success('Annotations saved!');
    } catch {
      toast.error('Failed to save annotations');
    }
  };

  const toggleShare = async () => {
    const newSharedState = !isShared;
    setIsShared(newSharedState);
    if (annotation?.id) {
      try {
        const endpoint = newSharedState ? 'share' : 'unshare';
        await axios.post(`${API}/annotations/${annotation.id}/${endpoint}`);
        toast.success(newSharedState ? 'Shared with teacher!' : 'Sharing removed');
      } catch {
        toast.error('Failed to update sharing');
      }
    }
  };

  if (showStudentModal) {
    return (
      <StudentLoginModal
        studentId={studentId} setStudentId={setStudentId}
        studentName={studentName} setStudentName={setStudentName}
        onContinue={handleStudentLogin}
      />
    );
  }

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" />
            <p className="mt-4 text-sm text-[#5A6577]">Loading source...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="bg-[#FDFBF7]">
        <NoteEditorModal
          open={showNoteModal}
          onOpenChange={setShowNoteModal}
          isEditing={!!editingNoteId}
          content={newNoteContent}
          setContent={setNewNoteContent}
          onSave={saveMarginNote}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate(`/primary-sources/${unitId}`)}
              className="text-[#5A6577] hover:text-[#14213D]"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Sources
            </Button>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">Annotation Tool</p>
              <p className="text-xs text-[#5A6577]">Student: {studentName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={isShared ? 'default' : 'outline'} size="sm" onClick={toggleShare} data-testid="share-btn">
              {isShared ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
              {isShared ? 'Shared' : 'Private'}
            </Button>
            <Button onClick={saveAnnotation} className="btn-primary" data-testid="save-btn">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AnnotationToolbar
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            selectedColor={selectedColor}
            setSelectedColor={setSelectedColor}
            drawingColor={drawingColor}
            setDrawingColor={setDrawingColor}
            strokeWidth={strokeWidth}
            setStrokeWidth={setStrokeWidth}
            showDrawingCanvas={showDrawingCanvas}
            setShowDrawingCanvas={setShowDrawingCanvas}
            onClearDrawings={clearDrawings}
          />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <SourceViewer
              source={source}
              activeTool={activeTool}
              showDrawingCanvas={showDrawingCanvas}
              canvasRef={canvasRef}
              marginNotes={marginNotes}
              teacherFeedback={teacherFeedback}
              renderHighlightedContent={renderHighlightedContent}
              onTextSelection={handleTextSelection}
              onAddMarginNote={handleAddMarginNote}
              onOpenNote={(note) => {
                setEditingNoteId(note.id);
                setNewNoteContent(note.content);
                setShowNoteModal(true);
              }}
              onStartDrawing={startDrawing}
              onDraw={draw}
              onStopDrawing={stopDrawing}
            />

            <div className="lg:col-span-1">
              <AnnotationSidebar
                highlights={highlights}
                marginNotes={marginNotes}
                drawings={drawings}
                onDeleteHighlight={deleteHighlight}
                onDeleteNote={deleteNote}
                onEditNote={(note) => {
                  setEditingNoteId(note.id);
                  setNewNoteContent(note.content);
                  setShowNoteModal(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
